/**
 * Follow-up task helpers shared by Home "My open tasks", the customer page
 * Tasks tab and the commit completion panel (dbo.FollowUpTasks via
 * /v3/follow-up-tasks). Pure — unit-tested.
 */
import type { FollowUpTask } from '@retail-ops/shared-types';

/**
 * Plain-language name for a follow-up task category. The payload tells an account-maintenance
 * e-statements task (Banno back office, CHG-0045) from an opening's verify task; the server card
 * uses the same words (CartProcessedCard.Label).
 */
export function followUpLabel(category: string, payloadJson?: string | null): string {
  if (category === 'estatement-verify' && payloadJson) {
    try {
      const p = JSON.parse(payloadJson) as Record<string, unknown> | null;
      if (p && p['reason'] === 'maintenance-estatements') {
        return p['direction'] === 'off'
          ? 'Remove e-statements in Banno back office'
          : 'Enroll in e-statements in Banno back office';
      }
    } catch {
      /* not JSON: use the category name */
    }
  }
  switch (category) {
    case 'foreign-ssn-followup': return 'Foreign-SSN follow-up';
    case 'deceased-followup': return 'Deceased customer follow-up';
    // Commit-completion auto-tasks (PipelineResumeFunction.CreateCompletionFollowUpTasksAsync,
    // Bill 2026-07-10). The debit-card-print task is auto-completed by a successful card
    // activation in CardIssuanceSection.
    case 'debit-card-print': return 'Print debit card';
    case 'check-order': return 'Order checks';
    // CartCommitService.FlagCipWriteFailureAsync (BACKLOG-0615): the CIP ID write to Core
    // Director failed, so the officer keys it in Core Director.
    case 'cip-core-entry': return 'Enter ID in Core Director';
    // Bill 2026-10-02 — a required signer with no signature and no remote link.
    case 'signature-outstanding': return 'Signature outstanding';
    case 'remote-signature': return 'Remote signature pending';
    // BACKLOG-1298 — completed from the Customer copy section of the completion panel.
    case 'disclosure-copies': return 'Give the customer their copies';
    // BACKLOG-1747 — tracked at commit (were reminders or missing).
    case 'aurora-upload': return 'Upload the signed documents to Aurora';
    case 'banno-enable': return 'Enable online banking in Banno back office';
    // CartCommitService: e-statements elected / W-9 outstanding.
    case 'estatement-verify': return 'Verify e-statement enrollment';
    case 'w9-followup': return 'W-9 follow-up';
    // CHG-0094 — no longer created (retired on ask #306, pcb-retail-api 3.37.1); kept so older rows still read plainly.
    case 'address-verification': return "Verify the customer's address";
    default: return category;
  }
}

export type FollowUpDestination =
  | { kind: 'cart'; id: string; path: string }
  | { kind: 'cif'; id: string; path: string };

function payloadCartId(payloadJson: string | undefined): string | null {
  if (!payloadJson) return null;
  try {
    const p = JSON.parse(payloadJson) as Record<string, unknown> | null;
    const v = p && typeof p === 'object' ? p['cartId'] : undefined;
    return typeof v === 'string' && v.trim() ? v.trim() : null;
  } catch {
    return null;
  }
}

/**
 * Where a task row opens: the cart it came from (payload cartId, or a task targeting a
 * cart), otherwise the customer page for a CIF task. null when the task points nowhere
 * the app can open.
 */
export function followUpDestination(
  task: Pick<FollowUpTask, 'targetEntityType' | 'targetEntityKey' | 'payloadJson'>,
): FollowUpDestination | null {
  const cartId = payloadCartId(task.payloadJson)
    ?? (task.targetEntityType === 'cart' && task.targetEntityKey ? task.targetEntityKey : null);
  if (cartId) return { kind: 'cart', id: cartId, path: `/rao/${encodeURIComponent(cartId)}` };
  if (task.targetEntityType === 'cif' && task.targetEntityKey) {
    return { kind: 'cif', id: task.targetEntityKey, path: `/rcm/${encodeURIComponent(task.targetEntityKey)}` };
  }
  return null;
}

/** True while the task still needs doing. */
export function followUpIsOpen(task: Pick<FollowUpTask, 'status'>): boolean {
  return task.status === 'open' || task.status === 'in-progress';
}

/** Signature state of a completion record, as far as the follow-up tasks care. */
export interface SignatureCounts {
  signaturesRequired?: number;
  signaturesCaptured?: number;
  signersOutstanding?: ReadonlyArray<unknown>;
}

const SIGNATURE_TASK_CATEGORIES: ReadonlySet<string> = new Set(['signature-outstanding', 'remote-signature']);

/** Every required signer is captured (the CHG-0138 recompute says so). */
export function allSignersCaptured(s: SignatureCounts | null | undefined): boolean {
  const required = typeof s?.signaturesRequired === 'number' ? s.signaturesRequired : 0;
  const captured = typeof s?.signaturesCaptured === 'number' ? s.signaturesCaptured : 0;
  return required > 0 && captured >= required && (s?.signersOutstanding?.length ?? 0) === 0;
}

/**
 * A key that changes whenever the completion record's signature counts change, so the commit
 * completion panel re-reads its follow-up tasks (666 test 2026-11-04: the server closed the
 * "Signature outstanding" task when signing finished, but the panel had read the list once at
 * commit and kept showing it with Sign in branch / Send remote link).
 */
export function signatureStateKey(s: SignatureCounts | null | undefined): string {
  return `${s?.signaturesCaptured ?? ''}/${s?.signaturesRequired ?? ''}/${s?.signersOutstanding?.length ?? ''}`;
}

/**
 * The follow-up tasks to show: once every signer is captured, the signature tasks are done even if
 * the list was read before the server closed them.
 */
export function visibleFollowUps<T extends Pick<FollowUpTask, 'taskCategory'>>(
  tasks: ReadonlyArray<T>,
  s: SignatureCounts | null | undefined,
): T[] {
  if (!allSignersCaptured(s)) return [...tasks];
  return tasks.filter((t) => !SIGNATURE_TASK_CATEGORIES.has(t.taskCategory));
}
